import { Dialog, DialogActions, DialogContent, DialogContentText, DialogTitle } from "@mui/material";
import React from "react";
import { useTranslation } from "react-i18next";
import { useToggle } from "react-use";

import { SharedButton } from "components/shared/Buttons/SharedButton";

import { ConfirmationDialogProvider } from ".";
import type { ConfirmationDialogContextPayload, ConfirmationDialogState, ShowConfirmationDialogOptions } from "./types";

const ConfirmationDialogContainer = ({ children }: { children: React.ReactNode }): JSX.Element => {
  const defaultState = React.useMemo<ConfirmationDialogState>(
    () => ({
      description: "",
      title: "",
      onConfirm: undefined,
      InputField: undefined,
      InputFieldProps: {},
      isValid: true,
    }),
    [],
  );

  const [confirmationDialogState, setConfirmationDialogState] = React.useState<ConfirmationDialogState>(defaultState);

  const [on, toggle] = useToggle(false);
  const { t } = useTranslation();

  const showConfirmationDialog = React.useCallback(
    (options: ShowConfirmationDialogOptions) => {
      const confirmationDialogOptions = options as ShowConfirmationDialogOptions;

      const newState = { ...defaultState, ...confirmationDialogOptions };

      setConfirmationDialogState(newState);
      toggle(true);
    },
    [defaultState, toggle],
  );

  const hideConfirmationDialog = React.useCallback(() => {
    toggle(false);
    setConfirmationDialogState(defaultState);
  }, [defaultState, toggle]);

  const confirmDialog = React.useCallback(() => {
    confirmationDialogState?.onConfirm?.();

    toggle(false);
    setConfirmationDialogState(defaultState);
  }, [confirmationDialogState, defaultState, toggle]);

  const updateComponentProps = React.useCallback(
    (props: ConfirmationDialogState) => {
      const componentProps = props ?? {};

      setConfirmationDialogState({ ...confirmationDialogState, ...componentProps });
    },
    [confirmationDialogState],
  );

  const payload = React.useMemo<ConfirmationDialogContextPayload>(
    () => ({
      confirmationDialogState,
      showConfirmationDialog,
      hideConfirmationDialog,
      updateComponentProps,
      isOpen: on,
    }),
    [confirmationDialogState, showConfirmationDialog, hideConfirmationDialog, updateComponentProps, on],
  );

  const InputField = confirmationDialogState?.InputField;

  return (
    <ConfirmationDialogProvider value={payload}>
      {children}

      <Dialog
        open={on}
        onClose={() => toggle(false)}
        disableEscapeKeyDown
        onClick={event => event.stopPropagation()}
        id="confirmationDialog"
      >
        <DialogTitle>{confirmationDialogState?.title}</DialogTitle>

        <DialogContent sx={{ paddingBottom: confirmationDialogState?.InputField ? 0 : "24px", minWidth: "500px" }}>
          <DialogContentText sx={{ whiteSpace: "pre-line" }}>{confirmationDialogState?.description}</DialogContentText>

          {InputField && <InputField {...confirmationDialogState?.InputFieldProps} />}
        </DialogContent>

        <DialogActions sx={{ paddingTop: confirmationDialogState?.InputField ? "16px !important" : "24px !important" }}>
          <SharedButton
            fullWidth
            color="error"
            onClick={() => toggle(false)}
            data-testid="cancelDialogButton"
          >
            {confirmationDialogState?.cancelLabel ?? t("buttons.cancel")}
          </SharedButton>

          <SharedButton
            fullWidth
            variant="contained"
            color="primary"
            id="confirmDialogButton"
            onClick={() => confirmDialog()}
            disabled={!confirmationDialogState?.isValid}
            data-testid="confirmDialogButton"
          >
            {confirmationDialogState?.confirmLabel ?? t("buttons.confirm")}
          </SharedButton>
        </DialogActions>
      </Dialog>
    </ConfirmationDialogProvider>
  );
};

export default ConfirmationDialogContainer;
